@file:UseSerializers(UuidSerializer::class)

package it.neckar.lizergy.model.configuration.quote

import it.neckar.lizergy.model.company.QuoteCompanyInformation
import it.neckar.lizergy.model.customer.Customer
import it.neckar.lizergy.model.financial.quote.QuoteElements
import it.neckar.lizergy.model.financial.quote.QuoteLegalNotice
import it.neckar.lizergy.model.financial.quote.QuoteNumberGenerator
import it.neckar.lizergy.model.financial.quote.QuoteSources
import it.neckar.open.time.nowMillis
import it.neckar.open.unit.si.ms
import it.neckar.uuid.HasValueClassUuid
import it.neckar.uuid.NativeUuid
import it.neckar.uuid.UuidSerializer
import it.neckar.uuid.ValueClassUuid
import it.neckar.uuid.randomUuid4
import kotlinx.serialization.Serializable
import kotlinx.serialization.UseSerializers
import kotlin.contracts.InvocationKind
import kotlin.contracts.contract
import kotlin.uuid.Uuid

const val quoteValidDuration: @ms Double = 1000 * 60 * 60 * 24 * 30.0

/**
 * Represents an actual quote.
 * A quote does *not* contain economics calculations
 */
@Serializable
data class Quote(
  override val id: QuoteId,

  /**
   * The company the quote has been created by
   */
  val sellingCompany: QuoteCompanyInformation,

  /**
   * The title of the quote
   */
  val title: String,

  /**
   * The name of the maintainer of the project
   */
  val maintainerName: String,

  /**
   * The customer
   */
  val customer: Customer,

  /**
   * The quote elements
   */
  val quoteElements: QuoteElements,

  /**
   * The sources of this quote
   */
  val sources: QuoteSources,

  /**
   * The creation date
   */
  val creationDate: @ms Double,

  /**
   * Until when the quote is valid
   */
  val validUntil: @ms Double = creationDate + quoteValidDuration,

  /**
   * More human-readable ID for this [Quote]
   */
  val quoteNumber: String = QuoteNumberGenerator.create(creationDate),

  /**
   * Additional information about delivery time
   */
  val deliveryTimeInMonths: Int = 5,

  /**
   * The legal notice
   * May contain multiple lines to be added to the end of the [Quote]
   */
  val legalNotice: QuoteLegalNotice = QuoteLegalNotice.Companion.empty,

  ) : HasValueClassUuid {

  /**
   * Is the current time past the valid until date
   */
  fun isOutdated(): Boolean {
    return nowMillis() > validUntil
  }

  /**
   * ID for a quote
   */
  @Serializable
  data class QuoteId(override val uuid: NativeUuid) : ValueClassUuid {

    override fun toString(): String {
      return uuid.toString()
    }

    fun format(): String {
      return uuid.toString()
    }

    companion object {
      fun random(): QuoteId {
        return QuoteId(randomUuid4())
      }
    }
  }

  /**
   * Executes the given block if there is a discount
   */
  fun ifDiscounted(action: () -> Unit) {
    contract {
      callsInPlace(action, InvocationKind.AT_MOST_ONCE)
    }

    return quoteElements.ifDiscounted(action)
  }
}
